// ---------------------------------------------------------------------------
// accordion — vanilla, ARIA-correct collapsible accordion. Used by the pricing
// FAQ and reused by Stage 5's FaqPage. Ports the legacy single-open
// `.accordion__question` behaviour from jqueryEvents.js (opening one entry
// closes the others) with a JS-free fallback: every panel is visible until this
// module runs, so the content is always readable without scripts.
//
// Markup contract:
//   <div data-accordion>                       (optional data-accordion-multi
//                                               to allow several open at once)
//     <… data-accordion-item>
//       <button data-accordion-trigger
//               aria-expanded="false"
//               aria-controls="<panelId>">…</button>
//       <div id="<panelId>" data-accordion-panel
//            role="region" aria-labelledby="<triggerId>">…</div>
//     </…>
//   </div>
// A trigger and its panel are paired by `aria-controls` → panel `id`. The open
// item carries `.is-open` on both trigger and panel, and ARIA mirrors it.
//
// Two things the panel's open state drives beyond `.is-open`:
//
//   - The disclosure chevron. The vendored stylesheet rotates it off
//     `.icon.rot` — eight copies of that rule across faq, pricing,
//     pricing-subpage, careers and download — never off `.is-open`, for which
//     it has no rules at all. components/_accordion.scss has said since it was
//     written that this module applies `.rot`; now it does.
//   - How the panel hides. `hidden` is `display: none`, which cannot be
//     transitioned, so panels that want the original's slideToggle opt out of
//     it with `data-accordion-panel="slide"` and are collapsed by
//     components/_accordion-motion.scss instead (which also takes their
//     contents out of the a11y tree). Panels without the value keep `hidden`.
// ---------------------------------------------------------------------------

type Pair = {
	trigger: HTMLElement;
	panel: HTMLElement;
	icon: HTMLElement | null;
	slide: boolean;
};

const setOpen = (pair: Pair, open: boolean): void => {
	pair.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
	pair.trigger.classList.toggle('is-open', open);
	pair.panel.classList.toggle('is-open', open);
	pair.icon?.classList.toggle('rot', open);
	if (!pair.slide) pair.panel.toggleAttribute('hidden', !open);
};

const initGroup = (group: HTMLElement): void => {
	const triggers = Array.from(group.querySelectorAll<HTMLElement>('[data-accordion-trigger]'));
	if (!triggers.length) return;

	const allowMultiple = group.hasAttribute('data-accordion-multi');

	const pairs: Pair[] = [];
	for (const trigger of triggers) {
		const id = trigger.getAttribute('aria-controls');
		const panel = id ? group.querySelector<HTMLElement>(`#${CSS.escape(id)}`) : null;
		if (!panel) continue;
		// The chevron is inside the trigger on faq and careers, but a *sibling*
		// of it on the download centre, where `.icon` and the trigger sit side by
		// side inside `.download__section__title`.
		const icon =
			trigger.querySelector<HTMLElement>('.icon') ??
			trigger.parentElement?.querySelector<HTMLElement>('.icon') ??
			null;
		pairs.push({ trigger, panel, icon, slide: panel.dataset.accordionPanel === 'slide' });
	}
	if (!pairs.length) return;

	// Closed by default once JS is in control (no-JS fallback left everything open).
	for (const pair of pairs) setOpen(pair, false);

	for (const pair of pairs) {
		pair.trigger.addEventListener('click', () => {
			const willOpen = pair.trigger.getAttribute('aria-expanded') !== 'true';
			if (willOpen && !allowMultiple) {
				for (const other of pairs) {
					if (other !== pair) setOpen(other, false);
				}
			}
			setOpen(pair, willOpen);
		});
	}

	group.classList.add('accordion-ready');
	// Transitions only from the next frame, so the no-JS-open → JS-closed switch
	// above lands without playing as an animation on every page load.
	requestAnimationFrame(() => group.classList.add('accordion-animated'));
};

export const initAccordion = (): void => {
	const groups = document.querySelectorAll<HTMLElement>('[data-accordion]');
	for (const group of groups) initGroup(group);
};
